import React from 'react';

export interface UnifiedCalendarItemProps {
  entry: {
    id: number;
    type: 'time_off' | 'task' | 'project';
    title: string;
    dueDate: string;
    status: string;
    request_type?: string; // for time_off entries
  };
  isDark: boolean;
  onClick?: () => void;
}

const UnifiedCalendarItem: React.FC<UnifiedCalendarItemProps> = ({
  entry,
  isDark,
  onClick,
}) => {
  // Color mapping for time_off by request_type
  const timeOffColorMap: Record<string, string> = {
    vacation: isDark
      ? 'bg-indigo-500/30 text-indigo-300'
      : 'bg-indigo-100 text-indigo-700',
    sick_leave: isDark
      ? 'bg-red-500/30 text-red-300'
      : 'bg-red-100 text-red-700',
    personal: isDark
      ? 'bg-purple-500/30 text-purple-300'
      : 'bg-purple-100 text-purple-700',
    emergency: isDark
      ? 'bg-orange-500/30 text-orange-300'
      : 'bg-orange-100 text-orange-700',
  };

  // Type badge styling
  const typeBadgeClass = {
    time_off: isDark ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800',
    task: isDark ? 'bg-teal-900 text-teal-200' : 'bg-teal-100 text-teal-800',
    project: isDark ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
  };

  // Background styling based on type
  const getBackgroundClass = (): string => {
    switch (entry.type) {
      case 'time_off':
        return (
          timeOffColorMap[entry.request_type || 'vacation'] ||
          (isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600')
        );
      case 'task':
        return isDark ? 'bg-teal-500/30 text-teal-300' : 'bg-teal-100 text-teal-700';
      case 'project':
        return isDark ? 'bg-green-500/30 text-green-300' : 'bg-green-100 text-green-700';
      default:
        return isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600';
    }
  };

  // Pending status yellow border
  const pendingBorder =
    entry.status === 'pending'
      ? isDark
        ? 'border-2 border-yellow-500'
        : 'border-2 border-yellow-400'
      : 'border-transparent';

  // Type label
  const typeLabel = {
    time_off: 'Time-Off',
    task: 'Task',
    project: 'Project',
  }[entry.type];

  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div
      onClick={onClick}
      className={`px-2 py-1.5 text-xs rounded border ${pendingBorder} ${getBackgroundClass()} cursor-pointer transition-opacity hover:opacity-80`}
      title={`${typeLabel} — ${entry.title}`}
    >
      <div className="flex items-center gap-1 truncate">
        <span
          className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${typeBadgeClass[entry.type]}`}
        >
          {typeLabel}
        </span>
        <span className="truncate font-medium">{entry.title}</span>
      </div>
      <div className="text-xs opacity-75 mt-0.5">{formatDate(entry.dueDate)}</div>
    </div>
  );
};

export default UnifiedCalendarItem;
